import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { authApi } from "@/api";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [googleClientId, setGoogleClientId] = useState("");
  const [googleLoading, setGoogleLoading] = useState(true);
  const navigate = useNavigate();
  const googleButtonRef = useRef<HTMLDivElement>(null);

  // Fetch Google Client ID and load GSI script
  useEffect(() => {
    let cancelled = false;

    authApi.getGoogleClientId().then(({ clientId }) => {
      if (cancelled) return;
      setGoogleLoading(false);
      if (!clientId) return;
      setGoogleClientId(clientId);
    }).catch(() => {
      if (!cancelled) setGoogleLoading(false);
    });

    return () => { cancelled = true; };
  }, []);

  // Load Google Identity Services script and render button
  useEffect(() => {
    if (!googleClientId) return;

    const handleCredentialResponse = async (response: { credential: string }) => {
      setError("");
      try {
        await authApi.googleLogin(response.credential);
        navigate("/");
      } catch (err: any) {
        setError(err?.message || "Googleログインに失敗しました");
      }
    };

    const initializeGoogle = () => {
      google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleCredentialResponse,
      });
      if (googleButtonRef.current) {
        google.accounts.id.renderButton(googleButtonRef.current, {
          theme: "outline",
          size: "large",
          width: 256,
          text: "signin_with",
          locale: "ja",
        });
      }
    };

    // Check if script is already loaded
    if (typeof google !== "undefined" && google.accounts?.id) {
      initializeGoogle();
      return;
    }

    // Load the script
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => initializeGoogle();
    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script if component unmounts before load
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [googleClientId, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await authApi.login(username, password);
      navigate("/");
    } catch {
      setError("ログインに失敗しました");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <div className="bg-white p-8 rounded-lg shadow-md w-80 space-y-4">
        <h1 className="text-xl font-bold text-center">SMS Gateway</h1>
        {error && (
          <p className="text-sm text-red-600 text-center">{error}</p>
        )}

        {/* Google SSO */}
        {!googleLoading && googleClientId && (
          <>
            <div ref={googleButtonRef} className="flex justify-center" />
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-300" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-2 text-slate-500">または</span>
              </div>
            </div>
          </>
        )}

        {/* Username/Password form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="ユーザー名"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="w-full px-3 py-2 border rounded-md text-sm"
          />
          <input
            type="password"
            placeholder="パスワード"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border rounded-md text-sm"
          />
          <button
            type="submit"
            className="w-full py-2 bg-slate-900 text-white rounded-md text-sm hover:bg-slate-800"
          >
            ログイン
          </button>
        </form>
      </div>
    </div>
  );
}
